/**
 * Pure helpers behind the catalog section and its dialog (TRA-1507), kept
 * out of the component files so they can be tested without a DOM.
 */

import type {
  CatalogModelCreate,
  CatalogModelView,
  CatalogPricing,
  CatalogProbeRequest,
  CatalogProbeResponse,
  CatalogResponse,
  CatalogUpstreamView,
} from '@/api/generated/model'

/** Mirrors CATALOG_SLUG_PATTERN in api/app/schemas/proxy_model.py. */
export const CATALOG_SLUG_REGEX =
  /^[a-z0-9]([a-z0-9.-]{0,62}[a-z0-9])?\/[a-z0-9]([a-z0-9.-]{0,62}[a-z0-9])?$/

/**
 * The one request-body param the catalog sets today. These are the
 * choices the select offers; a slot can carry any string the provider
 * accepts, and the form keeps whatever it finds there (see
 * initialCatalogForm), so a price-only edit never changes how a model
 * answers.
 */
export const REASONING_EFFORTS = ['none', 'minimal', 'low', 'medium', 'high', 'xhigh'] as const

export interface CatalogFormState {
  upstream_id: string
  slug: string
  display_name: string
  provider_model: string
  input_per_m: string
  cached_per_m: string
  output_per_m: string
  /** The slot's reasoning effort as the provider takes it, or '' for the provider's default. */
  reasoning_effort: string
  /** Whether the slot lists 'image' in its input modalities. */
  reads_images: boolean
}

export interface ParsedCatalogForm {
  pricing: CatalogPricing
  params: { [key: string]: unknown } | null
  input_modalities: CatalogModelCreate['input_modalities']
}

export function initialCatalogForm(model: CatalogModelView | null): CatalogFormState {
  const pricing = model?.pricing ?? null
  const effort = model?.params?.reasoning_effort
  const modalities = model?.input_modalities ?? null
  return {
    upstream_id: model?.upstream_id ?? '',
    slug: model?.slug ?? '',
    display_name: model?.display_name ?? '',
    provider_model: model?.provider_model ?? '',
    input_per_m: pricing === null ? '' : String(pricing.input_per_m),
    cached_per_m: pricing === null ? '' : String(pricing.cached_per_m),
    output_per_m: pricing === null ? '' : String(pricing.output_per_m),
    reasoning_effort: typeof effort === 'string' ? effort : '',
    reads_images: modalities !== null && modalities.includes('image'),
  }
}

function parsePrice(raw: string): number | null {
  const value = Number(raw)
  return raw.trim() === '' || !Number.isFinite(value) || value < 0 ? null : value
}

/** The slot params: the form's reasoning effort over whatever else the slot carried. */
export function catalogParams(
  form: Pick<CatalogFormState, 'reasoning_effort'>,
  existing: { [key: string]: unknown } | null | undefined,
): { [key: string]: unknown } | null {
  const { reasoning_effort: _dropped, ...kept } = existing ?? {}
  const params = form.reasoning_effort === '' ? kept : { ...kept, reasoning_effort: form.reasoning_effort }
  return Object.keys(params).length === 0 ? null : params
}

/** The request-shaped fields, or the message for the first field that fails. */
export function parseCatalogForm(
  form: CatalogFormState,
  existingParams: { [key: string]: unknown } | null = null,
): ParsedCatalogForm | string {
  const input = parsePrice(form.input_per_m)
  const cached = parsePrice(form.cached_per_m)
  const output = parsePrice(form.output_per_m)
  if (input === null || cached === null || output === null) {
    return 'Each price must be a number of dollars per 1M tokens, 0 or more.'
  }
  return {
    pricing: { input_per_m: input, cached_per_m: cached, output_per_m: output },
    params: catalogParams(form, existingParams),
    // Always explicit, so a save turns an undeclared slot into a declared one.
    input_modalities: form.reads_images ? ['text', 'image'] : ['text'],
  }
}

/**
 * What a sample call exercises: the upstream, the provider model, the
 * params and, when Reads images is checked, an image the reply must
 * describe. The dialog requires a passing call for this exact tuple before
 * it saves, so a change to any of them asks for another test.
 */
export function probeRequest(
  form: Pick<CatalogFormState, 'upstream_id' | 'provider_model' | 'reasoning_effort' | 'reads_images'>,
  model: Pick<CatalogModelView, 'slug' | 'params'> | null,
): CatalogProbeRequest {
  return {
    upstream_id: form.upstream_id,
    provider_model: form.provider_model.trim(),
    params: catalogParams(form, model?.params),
    // An edit probes the slot's own base URL, the origin its traffic reaches.
    slug: model === null ? null : model.slug,
    image: form.reads_images,
  }
}

/** The tuple a passing call vouches for. The image flag joins it only when set. */
export function probeKey(request: CatalogProbeRequest): string {
  const tuple: unknown[] = [request.upstream_id, request.provider_model, request.params]
  return JSON.stringify(request.image === true ? [...tuple, 'image'] : tuple)
}

/**
 * The sample call's result as one line: what the gateway asked, the answer,
 * then the verdict. The answer is `result.detail` as the gateway sends it
 * (already cut when long). The two questions restate probeMessages in
 * apps/inference-gateway/src/health.ts; change one, change both. `image` is
 * the flag the call went out with, not the checkbox as it stands now.
 *
 * Status 0 means the request never got the gateway's verdict (the api or
 * the network failed), so that line cannot say the question reached the
 * model and starts at "Failed".
 */
export function probeLine(image: boolean, result: CatalogProbeResponse): string {
  const asked = image
    ? 'Asked the color of a red test image.'
    : 'Asked the model to reply with the word ok.'
  const http = result.status > 0 ? ` (HTTP ${String(result.status)})` : ''
  if (result.ok) {
    // A reply often ends in its own period ("Red."); the line adds one.
    const answer = result.detail.replace(/\.$/, '')
    return `${asked} Answer: ${answer}. Passed${http}.`
  }
  const failed = `Failed${http}${result.detail === '' ? '' : `: ${result.detail}`}`
  return result.status > 0 ? `${asked} ${failed}` : failed
}

/**
 * Whether saving `request` needs a passing sample call first. A new model
 * always does. An edit does when it changes the upstream, the provider
 * model or the params, or when it checks Reads images on a slot that does
 * not list 'image'. The slot already serves the tuple it was opened with,
 * and a slot that reads images also answers text, so unchecking the box
 * needs no call.
 */
export function needsSampleCall(request: CatalogProbeRequest, model: CatalogModelView | null): boolean {
  if (model === null) return true
  const served = probeRequest(initialCatalogForm(model), model)
  const key = probeKey(request)
  return key !== probeKey(served) && key !== probeKey({ ...served, image: false })
}

export interface UpstreamGroup {
  upstream: CatalogUpstreamView
  models: CatalogModelView[]
}

/**
 * Models under their upstream, in upstream id order. A row whose upstream
 * is not public any more still shows, under a heading built from its id,
 * so nothing in the table is hidden.
 */
export function groupByUpstream(catalog: CatalogResponse): UpstreamGroup[] {
  const groups = new Map<string, UpstreamGroup>()
  for (const upstream of catalog.upstreams) {
    groups.set(upstream.id, { upstream, models: [] })
  }
  for (const model of catalog.models) {
    const group = groups.get(model.upstream_id)
    if (group) {
      group.models.push(model)
    } else {
      groups.set(model.upstream_id, {
        upstream: { id: model.upstream_id, name: model.upstream_id, type: '', base_url: '' },
        models: [model],
      })
    }
  }
  return [...groups.values()].sort((a, b) => a.upstream.id.localeCompare(b.upstream.id))
}

/** "$5" for a per-1M-token price; the em dash is the table's null placeholder. */
export function formatPrice(value: number | undefined): string {
  return value === undefined ? '—' : `$${String(value)}`
}

/** Whether the slot reads images, or the em dash when it declares nothing. */
export function formatReadsImages(model: CatalogModelView): string {
  if (model.input_modalities === null) return '—'
  return model.input_modalities.includes('image') ? 'Yes' : 'No'
}

/** The slot's reasoning effort for the table, or '' when the provider decides. */
export function formatReasoningEffort(model: CatalogModelView): string {
  const effort = model.params?.reasoning_effort
  return typeof effort === 'string' ? effort : ''
}
